import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AuthGate from '@/features/auth/components/AuthGate';
import type { LoadingStage } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requiredPermission?: string | null;
  requiredAnyPermissions?: string[] | null;
}

// Tempo mínimo de exibição do loading após login (ms)
const MIN_LOADING_DISPLAY_TIME = 2000;

// Intervalo entre estágios (em ms)
const STAGE_INTERVAL = 500;

// Chave do localStorage para verificar se há sessão potencial
const SESSION_CHECK_KEY = 'joshua_last_user_email';

// Chave para detectar se é um login fresco (não refresh)
const FRESH_LOGIN_KEY = 'joshua_fresh_login';

export default function ProtectedRoute({ children, requiredPermission = null, requiredAnyPermissions = null }: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission, user, loadingStage } = useAuth();

  // Verificar se há indicação de sessão anterior no localStorage
  const hasStoredSession = !!localStorage.getItem(SESSION_CHECK_KEY);

  // Estado para controlar exibição do loading
  // Só mostrar loading se for um login fresco, não em refresh
  const [showLoading, setShowLoading] = useState(() => {
    const freshLogin = !!sessionStorage.getItem(FRESH_LOGIN_KEY);
    return freshLogin && loading;
  });
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [animationStarted, setAnimationStarted] = useState(false);

  const loadingStartTimeRef = useRef<number>(0);
  const stageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic stage index: reconciles AuthContext's loadingStage (which drops
  // to null on completion) with the local 500ms timer, preventing the stepper
  // from regressing if auth finishes before currentStageIndex catches up.
  const stageHighWaterRef = useRef(-1);

  const stages: Exclude<LoadingStage, null>[] = [
    'authenticating',
    'loading-profile',
    'loading-permissions',
    'validating'
  ];

  // Limpar todos os timers
  const clearAllTimers = () => {
    if (stageTimerRef.current) {
      clearTimeout(stageTimerRef.current);
      stageTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  // Função para avançar para próximo estágio
  const advanceStage = () => {
    setCurrentStageIndex(prev => {
      const next = prev + 1;
      if (next < stages.length) {
        stageTimerRef.current = setTimeout(advanceStage, STAGE_INTERVAL);
      }
      return Math.min(next, stages.length - 1);
    });
  };

  // Iniciar animação de loading (apenas para login fresco)
  const startLoadingAnimation = () => {
    if (animationStarted) return;

    clearAllTimers();
    setCurrentStageIndex(0);
    stageHighWaterRef.current = -1;
    setShowLoading(true);
    setAnimationStarted(true);
    loadingStartTimeRef.current = Date.now();
    stageTimerRef.current = setTimeout(advanceStage, STAGE_INTERVAL);
  };

  // Efeito para gerenciar loading apenas em login fresco
  useEffect(() => {
    const freshLogin = !!sessionStorage.getItem(FRESH_LOGIN_KEY);

    // Só mostrar loading se for login fresco
    if (loading && freshLogin && !animationStarted) {
      startLoadingAnimation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, animationStarted]);

  // Quando loading muda para false
  useEffect(() => {
    if (!loading && animationStarted) {
      const elapsedTime = Date.now() - loadingStartTimeRef.current;
      const remainingTime = Math.max(0, MIN_LOADING_DISPLAY_TIME - elapsedTime);

      if (remainingTime > 0) {
        hideTimerRef.current = setTimeout(() => {
          clearAllTimers();
          setShowLoading(false);
          setAnimationStarted(false);
          // Limpar flag de login fresco
          sessionStorage.removeItem(FRESH_LOGIN_KEY);
        }, remainingTime);
      } else {
        clearAllTimers();
        setShowLoading(false);
        setAnimationStarted(false);
        sessionStorage.removeItem(FRESH_LOGIN_KEY);
      }
    }
  }, [loading, animationStarted]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => clearAllTimers();
  }, []);

  // Debug logging removido - expunha dados sensíveis

  // ==================== LÓGICA DE RENDERIZAÇÃO ====================

  // CASO 1: Sem sessão armazenada e não autenticado -> Login imediato
  if (!hasStoredSession && !isAuthenticated && !user) {
    return <AuthGate />;
  }

  // CASO 2: Login fresco com loading ativo -> Mostrar tela de carregamento
  if (showLoading && animationStarted) {
    const contextIdx = loadingStage ? stages.indexOf(loadingStage) : -1;
    const candidate = Math.max(contextIdx, currentStageIndex);
    if (candidate > stageHighWaterRef.current) {
      stageHighWaterRef.current = candidate;
    }
    const effectiveStage = stages[Math.min(stageHighWaterRef.current, stages.length - 1)];
    return <AuthGate loadingStage={effectiveStage} />;
  }

  // CASO 3: Refresh com sessão armazenada -> Mostrar app diretamente (sem loading)
  // O AuthContext vai validar em background
  // Se a sessão expirou, vai redirecionar para login após validação

  // CASO 4: Não autenticado após loading terminar -> Login
  if (!loading && !isAuthenticated) {
    // Limpar sessão armazenada já que expirou
    localStorage.removeItem(SESSION_CHECK_KEY);
    return <AuthGate />;
  }

  // CASO 5: Verificar permissões específicas
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        background: 'linear-gradient(135deg, var(--danger) 0%, #dc2626 100%)'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <span style={{ fontSize: '32px' }}>🚫</span>
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--text)',
            margin: '0 0 8px 0'
          }}>
            Acesso Negado
          </h2>
          <p style={{
            color: 'var(--muted)',
            fontSize: '16px',
            margin: '0 0 24px 0'
          }}>
            Você não possui permissão para acessar esta área do sistema.
          </p>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '12px 24px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // CASO 6: Verificar qualquer uma das permissões
  if (requiredAnyPermissions && !hasAnyPermission(requiredAnyPermissions)) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        background: 'linear-gradient(135deg, var(--danger) 0%, #dc2626 100%)'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <span style={{ fontSize: '32px' }}>🚫</span>
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--text)',
            margin: '0 0 8px 0'
          }}>
            Acesso Restrito
          </h2>
          <p style={{
            color: 'var(--muted)',
            fontSize: '16px',
            margin: '0 0 24px 0'
          }}>
            Você precisa de permissões específicas para acessar esta funcionalidade.
          </p>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '12px 24px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // CASO 7: Tudo OK -> Renderizar conteúdo protegido
  return children;
}
